import { __awaiter, __generator, __decorate, __param, __metadata } from 'tslib';
import { InjectionToken, Injectable, Inject, Injector, Component, NgModule } from '@angular/core';
import { assertIssuer, assertClientId, assertRedirectUri } from '@okta/configuration-validation';
import OktaAuth from '@okta/okta-auth-js';
import { Observable } from 'rxjs';

/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
var OKTA_CONFIG = new InjectionToken('okta.config.angular');

var packageInfo = {
    'name': '@okta/okta-angular',
    'version': '2.2.1'
};

/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
var OktaAuthService = /** @class */ (function () {
    function OktaAuthService(config, injector) {
        var _this = this;
        this.injector = injector;
        this.observers = [];
        /**
         * Cache the auth config.
         */
        this.config = Object.assign({}, config);
        this.config.scopes = this.config.scopes || ['openid', 'email'];
        /**
         * Scrub scopes to ensure 'openid' is included
         */
        this.scrubScopes(this.config.scopes);
        // Assert Configuration
        assertIssuer(this.config.issuer, this.config.testing);
        assertClientId(this.config.clientId);
        assertRedirectUri(this.config.redirectUri);
        this.oktaAuth = new OktaAuth(this.config);
        this.oktaAuth.userAgent = packageInfo.name + "/" + packageInfo.version + " " + this.oktaAuth.userAgent;
        this.$authenticationState = new Observable(function (observer) { _this.observers.push(observer); });
    }
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    OktaAuthService.prototype.login = function (fromUri, additionalParams) {
        this.setFromUri(fromUri);
        var onAuthRequired = this.config.onAuthRequired;
        if (onAuthRequired) {
            return onAuthRequired(this, this.injector);
        }
        return this.loginRedirect(undefined, additionalParams);
    };
    OktaAuthService.prototype.getTokenManager = function () {
        return this.oktaAuth.tokenManager;
    };
    /**
     * Checks if there is an access token OR an id token
     * A custom method may be provided on config to override this logic
     */
    OktaAuthService.prototype.isAuthenticated = function () {
        return __awaiter(this, void 0, void 0, function () {
            var accessToken, idToken;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // Support a user-provided method to check authentication
                        if (this.config.isAuthenticated) {
                            return [2 /*return*/, (this.config.isAuthenticated)(this)];
                        }
                        return [4 /*yield*/, this.getAccessToken()];
                    case 1:
                        accessToken = _a.sent();
                        return [4 /*yield*/, this.getIdToken()];
                    case 2:
                        idToken = _a.sent();
                        return [2 /*return*/, !!(accessToken || idToken)];
                }
            });
        });
    };
    OktaAuthService.prototype.emitAuthenticationState = function (state) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.observers.forEach(function (observer) { return observer.next(state); });
                return [2 /*return*/];
            });
        });
    };
    /**
     * Returns the current accessToken in the tokenManager.
     */
    OktaAuthService.prototype.getAccessToken = function () {
        return __awaiter(this, void 0, void 0, function () {
            var accessToken, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.oktaAuth.tokenManager.get('accessToken')];
                    case 1:
                        accessToken = _a.sent();
                        return [2 /*return*/, accessToken.accessToken];
                    case 2:
                        err_1 = _a.sent();
                        // The user no longer has an existing SSO session in the browser.
                        // (OIDC error `login_required`)
                        // Ask the user to authenticate again.
                        return [2 /*return*/, undefined];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns the current idToken in the tokenManager.
     */
    OktaAuthService.prototype.getIdToken = function () {
        return __awaiter(this, void 0, void 0, function () {
            var idToken, err_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.oktaAuth.tokenManager.get('idToken')];
                    case 1:
                        idToken = _a.sent();
                        return [2 /*return*/, idToken.idToken];
                    case 2:
                        err_2 = _a.sent();
                        // The user no longer has an existing SSO session in the browser.
                        // (OIDC error `login_required`)
                        // Ask the user to authenticate again.
                        return [2 /*return*/, undefined];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns user claims from the /userinfo endpoint.
     */
    OktaAuthService.prototype.getUser = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.oktaAuth.token.getUserInfo()];
            });
        });
    };
    /**
     * Returns the configuration object used.
     */
    OktaAuthService.prototype.getOktaConfig = function () {
        return this.config;
    };
    /**
     * Launches the login redirect.
     * @param fromUri
     * @param additionalParams
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    OktaAuthService.prototype.loginRedirect = function (fromUri, additionalParams) {
        if (fromUri) {
            this.setFromUri(fromUri);
        }
        var params = Object.assign({
            scopes: this.config.scopes,
            responseType: this.config.responseType
        }, additionalParams);
        return this.oktaAuth.token.getWithRedirect(params); // can throw
    };
    /**
     * Stores the intended path to redirect after successful login.
     * @param uri
     * @param queryParams
     */
    OktaAuthService.prototype.setFromUri = function (fromUri) {
        // Use current location if fromUri was not passed
        fromUri = fromUri || window.location.href;
        // If a relative path was passed, convert to absolute URI
        if (fromUri.charAt(0) === '/') {
            fromUri = window.location.origin + fromUri;
        }
        sessionStorage.setItem('referrerPath', fromUri);
    };
    /**
     * Returns the referrer path from localStorage or app root.
     */
    OktaAuthService.prototype.getFromUri = function () {
        var fromUri = sessionStorage.getItem('referrerPath') || window.location.origin;
        sessionStorage.removeItem('referrerPath');
        return fromUri;
    };
    /**
     * Parses the tokens from the callback URL.
     */
    OktaAuthService.prototype.handleAuthentication = function () {
        return __awaiter(this, void 0, void 0, function () {
            var res, tokens;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.oktaAuth.token.parseFromUrl()];
                    case 1:
                        res = _a.sent();
                        tokens = res.tokens;
                        if (tokens.accessToken) {
                            this.oktaAuth.tokenManager.add('accessToken', tokens.accessToken);
                        }
                        if (tokens.idToken) {
                            this.oktaAuth.tokenManager.add('idToken', tokens.idToken);
                        }
                        return [4 /*yield*/, this.isAuthenticated()];
                    case 2:
                        if (_a.sent()) {
                            this.emitAuthenticationState(true);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Clears the user session in Okta and removes
     * tokens stored in the tokenManager.
     * @param options
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
    OktaAuthService.prototype.logout = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var redirectUri;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        redirectUri = null;
                        options = options || {};
                        if (typeof options === 'string') {
                            redirectUri = options;
                            // If a relative path was passed, convert to absolute URI
                            if (redirectUri.charAt(0) === '/') {
                                redirectUri = window.location.origin + redirectUri;
                            }
                            options = {
                                postLogoutRedirectUri: redirectUri
                            };
                        }
                        return [4 /*yield*/, this.oktaAuth.signOut(options)];
                    case 1:
                        _a.sent();
                        this.emitAuthenticationState(false);
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Scrub scopes to ensure 'openid' is included
     * @param scopes
     */
    OktaAuthService.prototype.scrubScopes = function (scopes) {
        if (scopes.indexOf('openid') >= 0) {
            return;
        }
        scopes.unshift('openid');
    };
    OktaAuthService = __decorate([
        Injectable(),
        __param(0, Inject(OKTA_CONFIG)),
        __metadata("design:paramtypes", [Object, Injector])
    ], OktaAuthService);
    return OktaAuthService;
}());

/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
var OktaCallbackComponent = /** @class */ (function () {
    function OktaCallbackComponent(okta) {
        this.okta = okta;
    }
    OktaCallbackComponent.prototype.ngOnInit = function () {
        var _this = this;
        /**
         * Handles the response from Okta and parses tokens.
         */
        this.okta.handleAuthentication()
            .then(function () {
            /**
             * Navigate back to the saved uri, or root of application.
             */
            var fromUri = _this.okta.getFromUri();
            window.location.replace(fromUri);
        })
            .catch(function (e) {
            _this.error = e.toString();
        });
    };
    OktaCallbackComponent = __decorate([
        Component({
            template: "<div>{{error}}</div>"
        }),
        __metadata("design:paramtypes", [OktaAuthService])
    ], OktaCallbackComponent);
    return OktaCallbackComponent;
}());

/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
var OktaLoginRedirectComponent = /** @class */ (function () {
    function OktaLoginRedirectComponent(okta) {
        this.okta = okta;
    }
    OktaLoginRedirectComponent.prototype.ngOnInit = function () {
        this.okta.loginRedirect();
    };
    OktaLoginRedirectComponent = __decorate([
        Component({ template: "" }),
        __metadata("design:paramtypes", [OktaAuthService])
    ], OktaLoginRedirectComponent);
    return OktaLoginRedirectComponent;
}());

/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
var OktaAuthGuard = /** @class */ (function () {
    function OktaAuthGuard(oktaAuth, injector) {
        this.oktaAuth = oktaAuth;
        this.injector = injector;
    }
    /**
     * Gateway for protected route. Returns true if there is a valid accessToken,
     * otherwise it will cache the route and start the login flow.
     * @param route
     * @param state
     */
    OktaAuthGuard.prototype.canActivate = function (route, state) {
        return __awaiter(this, void 0, void 0, function () {
            var onAuthRequired;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.oktaAuth.isAuthenticated()];
                    case 1:
                        if (_a.sent()) {
                            return [2 /*return*/, true];
                        }
                        onAuthRequired = route.data['onAuthRequired'] || this.oktaAuth.getOktaConfig().onAuthRequired;
                        /**
                         * Store the current path
                         */
                        this.oktaAuth.setFromUri(state.url);
                        if (onAuthRequired) {
                            onAuthRequired(this.oktaAuth, this.injector);
                        }
                        else {
                            this.oktaAuth.loginRedirect();
                        }
                        return [2 /*return*/, false];
                }
            });
        });
    };
    OktaAuthGuard.prototype.canActivateChild = function (route, state) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.canActivate(route, state)];
            });
        });
    };
    OktaAuthGuard = __decorate([
        Injectable(),
        __metadata("design:paramtypes", [OktaAuthService, Injector])
    ], OktaAuthGuard);
    return OktaAuthGuard;
}());

function createOktaService(config, injector) {
    return new OktaAuthService(config, injector);
}

/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
var ɵ0 = createOktaService;
var OktaAuthModule = /** @class */ (function () {
    function OktaAuthModule() {
    }
    OktaAuthModule = __decorate([
        NgModule({
            declarations: [
                OktaCallbackComponent,
                OktaLoginRedirectComponent,
            ],
            exports: [
                OktaCallbackComponent,
                OktaLoginRedirectComponent,
            ],
            providers: [
                OktaAuthGuard,
                {
                    provide: OktaAuthService,
                    useFactory: ɵ0,
                    deps: [
                        OKTA_CONFIG,
                        Injector
                    ]
                }
            ]
        })
    ], OktaAuthModule);
    return OktaAuthModule;
}());

export { OKTA_CONFIG, OktaAuthGuard, OktaAuthModule, OktaAuthService, OktaCallbackComponent, OktaLoginRedirectComponent, createOktaService as ɵa };
//# sourceMappingURL=okta-angular.js.map
